import { createFocusTrap } from 'focus-trap';
import { openRestockModal } from '../../product/stoq.js';
import { addVariantToWishlist, WISHLIST_SELECT_VARIANT_EVENT } from '../../product/wishlist-button.js';
import { addVariantToCart, getVariantById } from './add-variant-to-cart.js';
/*
 * The same class `_js/theme/modal` toggles. The offcanvas drawers reach it
 * through `freeze-body-scrolling`, but that helper is built on jQuery, which
 * is a webpack external - importing it here would make this section bundle
 * depend on the global being in place before it runs.
 */ const FROZEN_CLASS = 'is-frozen';
/*
 * Turns the bar's transparent frame white while the sheet is up, so the two
 * read as one surface, and lifts it over the sheet's backdrop.
 */ const SHEET_OPEN_CLASS = 'is-sheet-open';
// Hides "notify me" on sold out rows, which save to the wishlist instead.
const WISHLIST_MODE_CLASS = 'is-wishlist-mode';
/*
 * Mobile-only bottom bar: an add to cart trigger that opens a sheet listing
 * every variant, plus the wishlist toggle. Desktop uses
 * `.GiftSetsPdpDetails-bar` instead, so only one of the two is ever visible.
 *
 * Returns a cleanup function for the section's `unload`.
 */ export const initMobileBar = (section)=>{
    const barEl = section.querySelector('.js-gift-sets-pdp-mobile-bar');
    const triggerEl = section.querySelector('.js-gift-sets-pdp-mobile-bar-trigger');
    const overlayEl = section.querySelector('.js-gift-sets-pdp-mobile-bar-overlay');
    const backdropEl = section.querySelector('.js-gift-sets-pdp-mobile-bar-backdrop');
    const errorEl = section.querySelector('.js-gift-sets-pdp-mobile-bar-error');
    const sheetTitleEl = section.querySelector('.js-gift-sets-pdp-mobile-bar-sheet-title');
    if (!barEl || !triggerEl || !overlayEl || !backdropEl) {
        return ()=>undefined;
    }
    // Re-bound so the hoisted `close` declaration below sees the narrowed types.
    const bar = barEl;
    const trigger = triggerEl;
    const overlay = overlayEl;
    const backdrop = backdropEl;
    const productJson = bar.dataset.productJson;
    const product = productJson ? JSON.parse(productJson) : null;
    if (!product) {
        return ()=>undefined;
    }
    const cleanups = [];
    let focusTrap = null;
    let isOpen = false;
    let isAdding = false;
    /*
   * 'cart' when opened from the add to cart trigger, 'wishlist' when the
   * wishlist toggle asks for a size: then every row adds to the wishlist.
   */ let mode = 'cart';
    let opener = trigger;
    var _sheetTitleEl_textContent;
    const cartTitle = (_sheetTitleEl_textContent = sheetTitleEl === null || sheetTitleEl === void 0 ? void 0 : sheetTitleEl.textContent) !== null && _sheetTitleEl_textContent !== void 0 ? _sheetTitleEl_textContent : '';
    const close = ()=>{
        if (!isOpen) {
            return;
        }
        isOpen = false;
        overlay.classList.remove('is-open', WISHLIST_MODE_CLASS);
        overlay.hidden = true;
        bar.classList.remove(SHEET_OPEN_CLASS);
        trigger.setAttribute('aria-expanded', 'false');
        document.body.classList.remove(FROZEN_CLASS);
        if (focusTrap) {
            try {
                focusTrap.deactivate();
            } catch (error) {
            // ignore
            }
            focusTrap = null;
        }
        opener.focus();
    };
    const open = (nextMode = 'cart', nextOpener = trigger)=>{
        if (isOpen) {
            return;
        }
        isOpen = true;
        mode = nextMode;
        opener = nextOpener;
        if (sheetTitleEl) {
            var _sheetTitleEl_dataset_wishlistTitle;
            sheetTitleEl.textContent = mode === 'wishlist' ? (_sheetTitleEl_dataset_wishlistTitle = sheetTitleEl.dataset.wishlistTitle) !== null && _sheetTitleEl_dataset_wishlistTitle !== void 0 ? _sheetTitleEl_dataset_wishlistTitle : cartTitle : cartTitle;
        }
        overlay.hidden = false;
        // Reflow between `hidden` and the class so the sheet animates in.
        void overlay.offsetHeight;
        overlay.classList.add('is-open');
        overlay.classList.toggle(WISHLIST_MODE_CLASS, mode === 'wishlist');
        bar.classList.add(SHEET_OPEN_CLASS);
        // Only the add to cart trigger declares the sheet it controls.
        if (opener === trigger) {
            trigger.setAttribute('aria-expanded', 'true');
        }
        document.body.classList.add(FROZEN_CLASS);
        try {
            // Also gives the sheet its ESC handling and click-outside close.
            focusTrap = createFocusTrap(overlay, {
                delayInitialFocus: false,
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                onDeactivate: close
            });
            focusTrap.activate();
        } catch (error) {
            // A sheet with no focusable rows is still usable; don't block opening.
            console.warn('[gift-sets-pdp-mobile-bar] focus-trap failed', error);
        }
    };
    const on = (el, type, handler)=>{
        el.addEventListener(type, handler);
        cleanups.push(()=>el.removeEventListener(type, handler));
    };
    on(trigger, 'click', ()=>open());
    on(backdrop, 'click', close);
    // The wishlist toggle has no size to save yet: ask for one here.
    on(bar, WISHLIST_SELECT_VARIANT_EVENT, (event)=>{
        event.preventDefault();
        open('wishlist', event.target);
    });
    /*
   * In wishlist mode every row, sold out or not, saves its variant. Returns
   * true when it handled the click, so the cart and notify handlers stop.
   */ const handleWishlistRow = (button)=>{
        if (mode !== 'wishlist') {
            return false;
        }
        const variantId = Number(button.dataset.variantId);
        close();
        if (Number.isFinite(variantId)) {
            addVariantToWishlist(variantId);
        }
        return true;
    };
    Array.from(overlay.querySelectorAll('.js-gift-sets-pdp-mobile-bar-variant')).forEach((button)=>{
        on(button, 'click', async ()=>{
            if (handleWishlistRow(button) || isAdding) {
                return;
            }
            const variant = getVariantById(product, Number(button.dataset.variantId));
            if (!variant) {
                return;
            }
            isAdding = true;
            if (errorEl) {
                errorEl.textContent = '';
            }
            // Close first, so the scroll lock is released before the cart drawer
            // takes it over.
            close();
            try {
                await addVariantToCart(product, variant);
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown error';
                if (errorEl) {
                    errorEl.textContent = message;
                }
            } finally{
                isAdding = false;
            }
        });
    });
    Array.from(overlay.querySelectorAll('.js-gift-sets-pdp-mobile-bar-notify')).forEach((button)=>{
        on(button, 'click', ()=>{
            if (handleWishlistRow(button)) {
                return;
            }
            const variantId = Number(button.dataset.variantId);
            if (!Number.isFinite(variantId)) {
                return;
            }
            // Restock Rocket applies its own scroll lock, so release ours first.
            close();
            openRestockModal(product, variantId);
        });
    });
    return ()=>{
        close();
        cleanups.forEach((cleanup)=>cleanup());
    };
};
